import React from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Chip,
  Button,
  Typography,
  Box,
  Link as MuiLink,
  Card,
} from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const ArchitecturalRequestsTable = ({
  rows = [],
  getStatusDetails,
  getRequestTypeName,
}) => {
  const safeRows = Array.isArray(rows)
    ? rows.filter((r) => r && typeof r.id !== "undefined")
    : [];

  const uniqueRows = Array.from(
    new Map(safeRows.map((item) => [item.id, item])).values()
  );

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 1.5,
    minWidth: 0,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const columns = [
    {
      field: "id",
      headerName: "Request ID",
      width: 130,
      renderCell: (params) =>
        params.row?.id ? (
          <MuiLink
            component={Link}
            to={`${params.row.id}`}
            underline="hover"
            sx={{
              fontWeight: 700,
              color: "#2f63ad",
              cursor: "pointer",
            }}
          >
            {`REQ-${params.row.id}`}
          </MuiLink>
        ) : (
          "N/A"
        ),
    },
    {
      field: "property_id",
      headerName: "Property ID",
      width: 130,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "fullname",
      headerName: "Member Name",
      width: 180,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2" noWrap sx={{ color: "#223142" }}>
            {params.value}
          </Typography>
        ) : (
          "N/A"
        ),
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 150,
      renderCell: (params) => params.value || "N/A",
    },
     {
      field: "status",
      headerName: "Status",
      width: 165,
      renderCell: (params) => {
        const status = params.row?.status || "";
        const { color = "default", Icon = null, label = status } =
          typeof getStatusDetails === "function"
            ? getStatusDetails(status)
            : {};

        return (
          <Chip
            icon={
              Icon ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Icon sx={{ color: "#fff !important", fontSize: 18 }} />
                </Box>
              ) : null
            }
            label={label ? label.toUpperCase() : status}
            sx={{
              backgroundColor: (theme) =>
                theme.palette[color]?.main || color || "#999",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "999px",
              height: 32,
              "& .MuiChip-label": {
                px: 1,
              },
              "& .MuiChip-icon": {
                color: "#fff",
                ml: 0.75,
              },
            }}
          />
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 220,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "full_address",
      headerName: "Address",
      width: 260,
      renderCell: (params) =>
        params.value ? (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.35,
              color: "#223142",
              py: 1,
            }}
          >
            {params.value}
          </Typography>
        ) : (
          "N/A"
        ),
    },
    {
      field: "request_type",
      headerName: "Request Type",
      width: 200,
      renderCell: (params) =>
        typeof getRequestTypeName === "function"
          ? getRequestTypeName(params.row?.request_type)
          : params.row?.request_type || "Unknown Type",
    },
   
    {
      field: "requested_at",
      headerName: "Request Date",
      width: 140,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          component={Link}
          to={`${params.row?.id}`}
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={secondaryButtonSx}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box sx={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={uniqueRows}
          columns={columns}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
                page: 0,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          slots={{
            toolbar: GridToolbar,
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-toolbarContainer": {
              p: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f7f9fc",
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              color: "#223142",
              fontWeight: 700,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(47,99,173,0.06)",
              color: "#223142",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(47,99,173,0.03)",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.01)",
            },
            "& .MuiTablePagination-root": {
              color: "#223142",
            },
            "& .MuiButton-root": {
              textTransform: "none",
            },
          }}
        />
      </Box>
    </Card>
  );
};

export default ArchitecturalRequestsTable;
